// ── BCHNostr — external video provider abstraction ────────────────
//
// BCHNostr is NOT a video host. The media always lives on someone else's
// infrastructure; this module is the seam between "a URL a creator pasted"
// and "something we can safely embed".
//
// Every provider answers the same four questions:
//
//   match(url)      is this URL mine?
//   parse(url)      what is the canonical id / watch page?
//   embed(id)       what do I put in an <iframe>, if anything?
//   thumbnail(id)   is there a predictable poster image?
//
// Adding a provider means adding one object to PROVIDERS. Nothing else in
// the video system needs to change — no parsing logic lives in components.
//
// SECURITY: a video URL is untrusted input that ends up in an `iframe src`
// or `video src`. Embeds are therefore built from a **fixed template plus
// a strictly-validated id**, never by interpolating the user's URL. The
// generic provider, which cannot do that, falls back to a direct
// `<video>` element and only for URLs that look like real media files.

// ── Types ─────────────────────────────────────────────────────────

export type ProviderId = 'youtube' | 'peertube' | 'odysee' | 'nostrmedia' | 'external';

/** How a resolved video should be played. */
export type PlaybackKind =
  /** Embed a third-party player in an iframe (YouTube, PeerTube). */
  | 'iframe'
  /** Play a direct media file in a native <video> element. */
  | 'file'
  /** We can't safely play it in-app; link out to the source instead. */
  | 'link';

export interface ResolvedVideo {
  provider: ProviderId;
  /** Provider-scoped id (YouTube video id, PeerTube uuid, or the URL). */
  id: string;
  /** Canonical page for the video on the provider (used for "watch on…"). */
  sourceUrl: string;
  /** How to play it. */
  playback: PlaybackKind;
  /** iframe src when playback === 'iframe'. */
  embedUrl: string;
  /** direct media src when playback === 'file'. */
  fileUrl: string;
  /** Predictable poster image, when the provider offers one. */
  thumbnailUrl: string;
  /** Human label, e.g. "YouTube". */
  label: string;
}

interface VideoProvider {
  id: ProviderId;
  label: string;
  /** Cheap test: could this URL belong to me? */
  match(url: URL): boolean;
  /** Full resolution. Return null if the URL matched but isn't playable. */
  resolve(url: URL): ResolvedVideo | null;
}

// ── Shared URL validation ─────────────────────────────────────────

/**
 * Parse a user-supplied string into an http(s) URL, or null.
 *
 * Rejects every other scheme (`javascript:`, `data:`, `file:`, `blob:`) and
 * URLs carrying credentials. This is the only place raw video URLs enter
 * the system.
 */
export function parseVideoUrl(raw: string | undefined | null): URL | null {
  if (!raw || typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value) return null;
  // Accept a bare host ("youtube.com/watch?v=…") as https.
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.username || url.password) return null;
    if (!url.hostname || !url.hostname.includes('.')) return null;
    return url;
  } catch {
    return null;
  }
}

/** True when `host` is `domain` or a subdomain of it. */
function isHost(host: string, domain: string): boolean {
  const h = host.toLowerCase().replace(/^www\./, '');
  return h === domain || h.endsWith(`.${domain}`);
}

/** File extensions we're willing to hand to a native <video> element. */
const MEDIA_EXT = /\.(mp4|webm|ogv|ogg|m4v|mov|m3u8|mpd)(\?|#|$)/i;

// ── YouTube ───────────────────────────────────────────────────────
//
// Covers youtube.com/watch?v=, /shorts/, /embed/, /live/, and youtu.be.
// The embed is built from `nocookie` + a validated 11-char id, so a hostile
// URL cannot inject anything into the iframe src.

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

const YouTubeProvider: VideoProvider = {
  id: 'youtube',
  label: 'YouTube',
  match: url =>
    isHost(url.hostname, 'youtube.com') ||
    isHost(url.hostname, 'youtu.be') ||
    isHost(url.hostname, 'youtube-nocookie.com'),
  resolve(url) {
    let id = '';

    if (isHost(url.hostname, 'youtu.be')) {
      id = url.pathname.slice(1).split('/')[0];
    } else {
      const v = url.searchParams.get('v');
      if (v) {
        id = v;
      } else {
        // /shorts/<id>, /embed/<id>, /live/<id>, /v/<id>
        const parts = url.pathname.split('/').filter(Boolean);
        const marker = parts.findIndex(p => ['shorts', 'embed', 'live', 'v'].includes(p));
        if (marker >= 0 && parts[marker + 1]) id = parts[marker + 1];
      }
    }

    if (!YOUTUBE_ID.test(id)) return null;

    return {
      provider: 'youtube',
      id,
      sourceUrl: `https://www.youtube.com/watch?v=${id}`,
      playback: 'iframe',
      // youtube-nocookie + no autoplay: we never autoplay, and we don't
      // hand YouTube a tracking cookie until the viewer presses play.
      embedUrl: `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`,
      fileUrl: '',
      thumbnailUrl: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
      label: 'YouTube',
    };
  },
};

// ── PeerTube ──────────────────────────────────────────────────────
//
// PeerTube is federated: the host is arbitrary, so we key off the URL
// SHAPE (/w/<id>, /videos/watch/<uuid>) rather than a domain allow-list.
// The instance host is validated as a hostname and the id against a
// conservative charset before either is placed in an embed URL.

const PEERTUBE_ID = /^[A-Za-z0-9-]{6,64}$/;
const SAFE_HOST = /^[a-z0-9.-]+\.[a-z]{2,}$/i;

const PeerTubeProvider: VideoProvider = {
  id: 'peertube',
  label: 'PeerTube',
  match: url => {
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts[0] === 'w' && parts[1]) return true;
    if (parts[0] === 'videos' && parts[1] === 'watch' && parts[2]) return true;
    if (parts[0] === 'videos' && parts[1] === 'embed' && parts[2]) return true;
    return false;
  },
  resolve(url) {
    const parts = url.pathname.split('/').filter(Boolean);
    const id = parts[0] === 'w' ? parts[1] : parts[2];
    if (!id || !PEERTUBE_ID.test(id)) return null;
    if (!SAFE_HOST.test(url.hostname)) return null;

    const host = url.hostname.toLowerCase();
    return {
      provider: 'peertube',
      id: `${host}/${id}`,
      sourceUrl: `https://${host}/w/${id}`,
      playback: 'iframe',
      embedUrl: `https://${host}/videos/embed/${id}?warningTitle=0&peertubeLink=0`,
      fileUrl: '',
      // PeerTube thumbnails aren't reliably at a fixed path across
      // versions, so we let the creator's own thumbnail win instead of
      // guessing and rendering a broken image.
      thumbnailUrl: '',
      label: 'PeerTube',
    };
  },
};

// ── Odysee (LBRY) ─────────────────────────────────────────────────
//
// Odysee URLs identify a "claim" by name + claim-id, in a few shapes:
//
//   https://odysee.com/@Channel:c/video-name:d      (channel form)
//   https://odysee.com/video-name:claimid           (anonymous form)
//   https://odysee.com/$/embed/@Channel:c/name:d     (an embed link,
//                                                     sometimes pasted)
//
// Legacy lbry.tv links resolve the same way. The player embeds at
// `odysee.com/$/embed/<...claim path...>`.
//
// Real Odysee claim names are surprisingly permissive: alongside letters,
// digits, `@`, `:` and `.` they routinely contain `-`, `,`, `(`, `)`,
// apostrophes, and percent-encoded UTF-8 (`%C3%81`). The original strict
// allow-list rejected all of those, so a title like
//   @channel:b/TRUMP-ESTÁ-con-Pilar,-Carlos-(720p):1
// failed to resolve and the video silently fell through to "link out".
//
// SECURITY: rather than enumerate the ever-growing set of legal name
// characters, each segment is rejected only if it contains a character that
// could break out of a single URL path segment — the path/query/fragment
// separators and anything that could smuggle markup or a new scheme. The
// segments are then re-encoded with encodeURIComponent (which leaves the
// meaningful `@`, `:`, `-`, `.`, `_`, `~`, `!`, `*`, `'`, `(`, `)` intact)
// and reassembled on the FIXED odysee.com host. The raw input host, query
// string and fragment are discarded, so nothing hostile reaches the iframe
// src regardless of what the creator pasted.

// Characters that must never appear literally inside a single claim segment:
// path/query/fragment separators, whitespace, and quote/angle characters
// that could break out of an attribute if this ever landed somewhere unsafe.
const ODYSEE_FORBIDDEN = /[/?#\\<>"'`\s\u0000-\u001f]/;

/** Encode one already-validated claim segment for use in a URL path. */
function encodeOdyseeSegment(segment: string): string {
  // The segment may already be percent-encoded (e.g. "%C3%81"). Decode
  // first so we don't double-encode, then re-encode, preserving the two
  // separators Odysee gives structural meaning to: "@" (channel prefix)
  // and ":" (name/claim-id divider).
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // Malformed %-escape: encode the raw bytes as-is.
    decoded = segment;
  }
  return encodeURIComponent(decoded)
    .replace(/%40/g, '@')
    .replace(/%3A/gi, ':');
}

const OdyseeProvider: VideoProvider = {
  id: 'odysee',
  label: 'Odysee',
  match: url => isHost(url.hostname, 'odysee.com') || isHost(url.hostname, 'lbry.tv'),
  resolve(url) {
    let parts = url.pathname.split('/').filter(Boolean);
    // Strip a leading "$/embed" (or "$/download") wrapper if the creator
    // pasted an embed link rather than the watch page.
    if (parts[0] === '$' && parts[1] && (parts[1] === 'embed' || parts[1] === 'download')) {
      parts = parts.slice(2);
    } else if (parts[0] === '$') {
      // Any other $/… route (search, settings, …) isn't a video.
      return null;
    }

    // A valid claim path is 1 segment (anonymous) or 2 (channel + video).
    if (parts.length < 1 || parts.length > 2) return null;
    // Reject anything with an obviously dangerous character or an
    // unreasonable length; the rest of the charset is left permissive so
    // real-world video names (commas, parens, accents…) resolve.
    if (!parts.every(p => p.length >= 1 && p.length <= 160 && !ODYSEE_FORBIDDEN.test(p))) {
      return null;
    }

    // The final segment must actually name a claim: <name>:<id> or a bare
    // <name>. A lone "@channel" (a channel page, no video) is rejected.
    const last = parts[parts.length - 1];
    if (last.startsWith('@') && parts.length === 1) return null;

    // Rebuild the path from re-encoded segments on the fixed odysee.com host.
    const claimPath = parts.map(encodeOdyseeSegment).join('/');

    return {
      provider: 'odysee',
      id: claimPath,
      sourceUrl: `https://odysee.com/${claimPath}`,
      playback: 'iframe',
      embedUrl: `https://odysee.com/$/embed/${claimPath}`,
      // Odysee thumbnails live on odycdn under a per-claim path we can't
      // derive from the URL alone, so we let the creator's own thumbnail
      // win rather than guess and render a broken image.
      thumbnailUrl: '',
      label: 'Odysee',
    };
  },
};

// ── Nostr-native media (Blossom / NIP-96) ─────────────────────────
//
// Blossom serves blobs at /<sha256>[.ext]; NIP-96 servers commonly do the
// same. These are direct media files, so they play in a native <video>
// element — no iframe, no third party in the page.

const BLOSSOM_PATH = /^\/([0-9a-f]{64})(\.[a-z0-9]{2,5})?$/i;

const NostrMediaProvider: VideoProvider = {
  id: 'nostrmedia',
  label: 'Nostr media',
  match: url => BLOSSOM_PATH.test(url.pathname),
  resolve(url) {
    const m = url.pathname.match(BLOSSOM_PATH);
    if (!m) return null;
    const hash = m[1].toLowerCase();
    const ext = (m[2] || '').toLowerCase();
    // A bare hash with no extension could be any blob type. We accept it —
    // Blossom's convention is extension-optional — but a non-media
    // extension is a clear "this isn't a video".
    if (ext && !MEDIA_EXT.test(ext)) return null;

    return {
      provider: 'nostrmedia',
      id: hash,
      sourceUrl: url.toString(),
      playback: 'file',
      embedUrl: '',
      fileUrl: url.toString(),
      thumbnailUrl: '',
      label: 'Nostr media',
    };
  },
};

// ── Generic external video ────────────────────────────────────────
//
// The catch-all. We will only play a URL natively when it actually looks
// like a media file; anything else (an arbitrary web page) is NOT embedded
// — we keep the BCHNostr page, discussion, and payments, and link out for
// playback. Embedding arbitrary third-party pages in an iframe would be a
// standing invitation to clickjacking and framed phishing.

const GenericProvider: VideoProvider = {
  id: 'external',
  label: 'External',
  match: () => true,
  resolve(url) {
    const isMedia = MEDIA_EXT.test(url.pathname);
    return {
      provider: 'external',
      id: url.toString(),
      sourceUrl: url.toString(),
      playback: isMedia ? 'file' : 'link',
      embedUrl: '',
      fileUrl: isMedia ? url.toString() : '',
      thumbnailUrl: '',
      label: 'External',
    };
  },
};

// ── Registry ──────────────────────────────────────────────────────
//
// Order matters: specific providers first, generic last. To add a provider
// (Rumble, Odysee, a Nostr-native video protocol…), implement the
// interface and insert it before GenericProvider.

const PROVIDERS: VideoProvider[] = [
  YouTubeProvider,
  PeerTubeProvider,
  OdyseeProvider,
  NostrMediaProvider,
  GenericProvider,
];

/**
 * Resolve any user-supplied video URL into something playable.
 *
 * Returns null only when the URL isn't a usable http(s) URL at all. A
 * matched-but-unplayable URL degrades to `playback: 'link'` rather than
 * failing, so the BCHNostr page around it still works.
 */
export function resolveVideoUrl(raw: string | undefined | null): ResolvedVideo | null {
  const url = parseVideoUrl(raw);
  if (!url) return null;

  for (const provider of PROVIDERS) {
    if (!provider.match(url)) continue;
    const resolved = provider.resolve(url);
    if (resolved) return resolved;
    // Matched the shape but failed to resolve (e.g. a YouTube URL with no
    // video id, like a channel page). Keep trying — GenericProvider will
    // catch it and at worst produce a 'link'.
  }

  return GenericProvider.resolve(url);
}

/** Is this string a video URL we can do anything useful with? */
export function isSupportedVideoUrl(raw: string): boolean {
  const resolved = resolveVideoUrl(raw);
  return !!resolved && resolved.playback !== 'link';
}

/** Provider label for a resolved id, for UI badges. */
export function providerLabel(provider: ProviderId): string {
  return PROVIDERS.find(p => p.id === provider)?.label || 'External';
}

/**
 * Best-effort thumbnail for a URL, used to pre-fill the Add Video form.
 *
 * Only providers with a *predictable* poster path return one (YouTube).
 * Guessing wrong would render a broken image, so the others return '' and
 * let the creator supply their own.
 */
export function guessThumbnail(raw: string): string {
  return resolveVideoUrl(raw)?.thumbnailUrl || '';
}

// ── Metadata lookup ───────────────────────────────────────────────

export interface FetchedMetadata {
  title: string;
  description: string;
  thumbnailUrl: string;
  duration: number;
  author: string;
}

/**
 * Try to fetch title/thumbnail for a URL from the provider.
 *
 * Uses **oEmbed** where the provider publishes it (YouTube, PeerTube),
 * which is a small, well-defined JSON document — we never scrape HTML.
 * Podcast-style hosts don't send CORS headers, so the request goes through
 * the shared read-only proxy.
 *
 * Never throws and never blocks: on any failure the creator just fills the
 * form in by hand.
 */
export async function fetchVideoMetadata(raw: string): Promise<FetchedMetadata | null> {
  const resolved = resolveVideoUrl(raw);
  if (!resolved) return null;

  const oembed = oembedEndpoint(resolved);
  if (!oembed) {
    // No metadata source, but a predictable thumbnail may still exist.
    return resolved.thumbnailUrl
      ? { title: '', description: '', thumbnailUrl: resolved.thumbnailUrl, duration: 0, author: '' }
      : null;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try {
    const res = await fetch(
      `https://proxy.shakespeare.diy/?url=${encodeURIComponent(oembed)}`,
      { signal: controller.signal },
    );
    if (!res.ok) throw new Error('lookup failed');

    const raw: unknown = await res.json();
    if (!raw || typeof raw !== 'object') throw new Error('bad payload');
    const data = raw as Record<string, unknown>;

    const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

    return {
      title: str(data.title).slice(0, 300),
      // oEmbed has no description field; PeerTube adds one on some versions.
      description: str(data.description).slice(0, 3000),
      // Re-validate: the thumbnail comes from a third party.
      thumbnailUrl: parseVideoUrl(str(data.thumbnail_url))?.toString()
        || resolved.thumbnailUrl,
      duration: Math.max(0, Math.floor(num(data.duration))),
      author: str(data.author_name).slice(0, 200),
    };
  } catch {
    return resolved.thumbnailUrl
      ? { title: '', description: '', thumbnailUrl: resolved.thumbnailUrl, duration: 0, author: '' }
      : null;
  } finally {
    clearTimeout(timer);
  }
}

/** The provider's oEmbed endpoint for this video, or '' if it has none. */
function oembedEndpoint(video: ResolvedVideo): string {
  switch (video.provider) {
    case 'youtube':
      return `https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(video.sourceUrl)}`;
    case 'peertube': {
      // Built from the already-validated host, not from raw input.
      const host = video.id.split('/')[0];
      if (!SAFE_HOST.test(host)) return '';
      return `https://${host}/services/oembed?format=json&url=${encodeURIComponent(video.sourceUrl)}`;
    }
    case 'odysee':
      // Odysee publishes oEmbed at a fixed endpoint; sourceUrl was rebuilt
      // from validated segments, so it's safe to hand back to them.
      return `https://odysee.com/$/oembed?format=json&url=${encodeURIComponent(video.sourceUrl)}`;
    default:
      return '';
  }
}
